import React, { useEffect, useState } from 'react';
import { trans, transModule } from '../../../_providers/Translation';
import Api from '../../../_helpers/Api';
import NoDataFound from '../../partials/NoDataFound';

function PriceCell({ editMode, value, editedValue, onChange, currency, saving }) {
    if (editMode) {
        return (
            <>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editedValue ?? ''}
                    onChange={e => onChange(e.target.value)}
                    style={{ width: 100 }}
                    disabled={saving}
                />{' '}
                <span>{currency || '-'}</span>
            </>
        );
    }
    if (value != null) {
        return <>{value}{' '}<span>{currency || '-'}</span></>;
    }
    return <>{trans('messages.price_not_set_yet') || 'Price not set yet'}</>;
}

function Section({ title, editMode, onEdit, onSave, onCancel, saving, saveError, children, hasData }) {
    return (
        <div style={{ marginBottom: 40 }}>
            <div className="top-part" style={{ marginBottom: 10 }}>
                <div className="left">
                    <h2 style={{ fontSize: 18, fontWeight: 600 }}>{title}</h2>
                </div>
                <div className="right">
                    {!editMode && hasData && (
                        <button className="button button-edit" onClick={onEdit}>
                            {trans('buttons.edit') || 'Edit'}
                        </button>
                    )}
                    {editMode && (
                        <>
                            <button className="button button-save" onClick={onSave} disabled={saving}>
                                {saving ? (trans('buttons.saving') || 'Saving...') : (trans('buttons.save') || 'Save')}
                            </button>
                            <button className="button button-cancel" onClick={onCancel} disabled={saving}>
                                {trans('buttons.cancel') || 'Cancel'}
                            </button>
                        </>
                    )}
                </div>
            </div>
            {saveError && <p style={{ color: 'red' }}>{saveError}</p>}
            {children}
        </div>
    );
}

function Configurator() {
    // --- Modules (existing) ---
    const [modules, setModules] = useState([]);
    const [modulesLoading, setModulesLoading] = useState(true);
    const [modulesError, setModulesError] = useState(null);
    const [modulesEditMode, setModulesEditMode] = useState(false);
    const [modulesEdited, setModulesEdited] = useState({});
    const [modulesSaving, setModulesSaving] = useState(false);
    const [modulesSaveError, setModulesSaveError] = useState(null);

    // --- Bundle ---
    const [bundlePrice, setBundlePrice] = useState(null);
    const [compulsoryModules, setCompulsoryModules] = useState([]);
    const [bundleLoading, setBundleLoading] = useState(true);
    const [bundleError, setBundleError] = useState(null);
    const [bundleEditMode, setBundleEditMode] = useState(false);
    const [bundlePriceEdited, setBundlePriceEdited] = useState('');
    const [bundleSaving, setBundleSaving] = useState(false);
    const [bundleSaveError, setBundleSaveError] = useState(null);

    // --- Employee prices ---
    const [employeeTiers, setEmployeeTiers] = useState([]);
    const [tiersLoading, setTiersLoading] = useState(true);
    const [tiersError, setTiersError] = useState(null);
    const [tiersEditMode, setTiersEditMode] = useState(false);
    const [tiersEdited, setTiersEdited] = useState({});
    const [tiersSaving, setTiersSaving] = useState(false);
    const [tiersSaveError, setTiersSaveError] = useState(null);

    useEffect(() => {
        // Modules
        Api.get('/admin/module-pricing/all')
            .then(res => setModules(res.data || []))
            .catch(() => setModulesError(trans('headings.error6')))
            .finally(() => setModulesLoading(false));

        // Bundle
        Api.get('licenses/bundle-pricing/all')
            .then(res => {
                setBundlePrice(res.data?.bundle_price ?? null);
                setCompulsoryModules(res.data?.compulsory_modules || []);
            })
            .catch(() => setBundleError(trans('headings.error6')))
            .finally(() => setBundleLoading(false));

        // Employee prices
        Api.get('licenses/employee-prices/all')
            .then(res => setEmployeeTiers(Array.isArray(res.data) ? res.data : []))
            .catch(() => setTiersError(trans('headings.error6')))
            .finally(() => setTiersLoading(false));
    }, []);

    // --- Modules handlers ---
    const handleModulesEdit = () => {
        const prices = {};
        modules.forEach(row => { prices[row.id] = row.price_per_month; });
        setModulesEdited(prices);
        setModulesEditMode(true);
    };
    const handleModulesSave = () => {
        setModulesSaving(true);
        setModulesSaveError(null);
        const prices = Object.keys(modulesEdited).map(id => ({
            id,
            price_per_month: modulesEdited[id] !== '' && modulesEdited[id] != null ? Number(modulesEdited[id]).toFixed(2) : null
        }));
        const formData = new FormData();
        prices.forEach((item, i) => {
            formData.append(`prices[${i}][id]`, item.id);
            if (item.price_per_month != null) formData.append(`prices[${i}][price_per_month]`, item.price_per_month);
        });
        Api.post('/admin/module-pricing/bulk-update', formData)
            .then(() => {
                setModules(prev => prev.map(row => ({
                    ...row,
                    price_per_month: modulesEdited[row.id] != null && modulesEdited[row.id] !== '' ? Number(modulesEdited[row.id]).toFixed(2) : null
                })));
                setModulesEditMode(false);
            })
            .catch(() => setModulesSaveError('Failed to save prices.'))
            .finally(() => setModulesSaving(false));
    };
    const handleModulesCancel = () => { setModulesEditMode(false); setModulesEdited({}); setModulesSaveError(null); };

    // --- Bundle handlers ---
    const handleBundleEdit = () => {
        setBundlePriceEdited(bundlePrice ?? '');
        setBundleEditMode(true);
    };
    const handleBundleSave = () => {
        setBundleSaving(true);
        setBundleSaveError(null);
        const formData = new FormData();
        if (bundlePriceEdited !== '' && bundlePriceEdited != null) {
            formData.append('value', Number(bundlePriceEdited).toFixed(2));
        }
        Api.post('licenses/bundle-pricing/update', formData)
            .then(() => {
                setBundlePrice(bundlePriceEdited !== '' && bundlePriceEdited != null ? Number(bundlePriceEdited).toFixed(2) : null);
                setBundleEditMode(false);
            })
            .catch(() => setBundleSaveError('Failed to save prices.'))
            .finally(() => setBundleSaving(false));
    };
    const handleBundleCancel = () => { setBundleEditMode(false); setBundlePriceEdited(''); setBundleSaveError(null); };

    // --- Employee prices handlers ---
    const handleTiersEdit = () => {
        const prices = {};
        employeeTiers.forEach(row => { prices[row.id] = row.price; });
        setTiersEdited(prices);
        setTiersEditMode(true);
    };
    const handleTiersSave = () => {
        setTiersSaving(true);
        setTiersSaveError(null);
        const formData = new FormData();
        employeeTiers.forEach((row, i) => {
            const editedPrice = tiersEdited[row.id];
            const price = editedPrice !== '' && editedPrice != null ? Number(editedPrice).toFixed(2) : null;
            formData.append(`prices[${i}][id]`, row.id);
            formData.append(`prices[${i}][number_of_employees]`, row.number_of_employees);
            if (price != null) formData.append(`prices[${i}][price]`, price);
            if (row.currency_id) formData.append(`prices[${i}][currency_id]`, row.currency_id);
        });
        Api.post('licenses/employee-prices/bulk-update', formData)
            .then(() => {
                setEmployeeTiers(prev => prev.map(row => ({
                    ...row,
                    price: tiersEdited[row.id] != null && tiersEdited[row.id] !== '' ? Number(tiersEdited[row.id]).toFixed(2) : null
                })));
                setTiersEditMode(false);
            })
            .catch(() => setTiersSaveError('Failed to save prices.'))
            .finally(() => setTiersSaving(false));
    };
    const handleTiersCancel = () => { setTiersEditMode(false); setTiersEdited({}); setTiersSaveError(null); };

    const currency = modules[0]?.currency?.name || '';

    const moduleTableHeaders = (
        <tr>
            <th>{trans('labels.module') || 'Module'}</th>
            <th>{trans('labels.price_per_month') || 'Price / Month'}</th>
            <th>{trans('labels.price_per_3_months') || '3 Months'}</th>
            <th>{trans('labels.price_per_6_months') || '6 Months'}</th>
            <th>{trans('labels.price_per_9_months') || '9 Months'}</th>
            <th>{trans('labels.price_per_12_months') || '12 Months'}</th>
        </tr>
    );

    const renderModuleRow = (row, idx, editedPrices, onPriceChange, editMode, saving) => (
        <tr key={row.id || idx}>
            <td>{row.module?.name ? transModule(row.module.name) : '-'}</td>
            <td>
                <PriceCell
                    editMode={editMode}
                    value={row.price_per_month}
                    editedValue={editedPrices[row.id]}
                    onChange={val => onPriceChange(row.id, val)}
                    currency={row.currency?.name}
                    saving={saving}
                />
            </td>
            <td>{row.price_per_month != null ? `${Number(row.price_per_month * 3).toFixed(2)} ${row.currency?.name || '-'}` : (trans('messages.price_not_set_yet') || 'Price not set yet')}</td>
            <td>{row.price_per_month != null ? `${Number(row.price_per_month * 6).toFixed(2)} ${row.currency?.name || '-'}` : (trans('messages.price_not_set_yet') || 'Price not set yet')}</td>
            <td>{row.price_per_month != null ? `${Number(row.price_per_month * 9).toFixed(2)} ${row.currency?.name || '-'}` : (trans('messages.price_not_set_yet') || 'Price not set yet')}</td>
            <td>{row.price_per_month != null ? `${Number(row.price_per_month * 12).toFixed(2)} ${row.currency?.name || '-'}` : (trans('messages.price_not_set_yet') || 'Price not set yet')}</td>
        </tr>
    );

    return (
        <div className="wrapper">
            <div className="top-part">
                <div className="left">
                    <h1 className="page-heading">
                        {trans('headings.price_configurator_custom_license') || 'Licenses Configurator'}
                    </h1>
                    <p className="page-subheading">
                        {trans('subheadings.price_configurator_custom_license') || 'Manage license configuration here.'}
                    </p>
                </div>
            </div>

            {/* Section 1: Employee tiers */}
            <Section
                title={trans('headings.prices_per_employees_license') || 'Custom prices per employees in license'}
                editMode={tiersEditMode}
                onEdit={handleTiersEdit}
                onSave={handleTiersSave}
                onCancel={handleTiersCancel}
                saving={tiersSaving}
                saveError={tiersSaveError}
                hasData={employeeTiers.length > 0}
            >
                {tiersLoading ? (
                    <p>{trans('labels.loading') || 'Loading...'}</p>
                ) : tiersError ? (
                    <p style={{ color: 'red' }}>{tiersError}</p>
                ) : employeeTiers.length === 0 ? (
                    <NoDataFound />
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>{trans('labels.numOfEmployees') || 'Employees'}</th>
                                <th>{trans('labels.price_per_month') || 'Price / Month'}</th>
                                <th>{trans('labels.price_per_3_months') || '3 Months'}</th>
                                <th>{trans('labels.price_per_6_months') || '6 Months'}</th>
                                <th>{trans('labels.price_per_9_months') || '9 Months'}</th>
                                <th>{trans('labels.price_per_12_months') || '12 Months'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employeeTiers.map((row, idx) => {
                                const val = tiersEditMode ? tiersEdited[row.id] : row.price;
                                return (
                                    <tr key={row.id || idx}>
                                        <td>{row.number_of_employees == 0 ? (trans('labels.unlimited') || 'Unlimited') : row.number_of_employees}</td>
                                        <td>
                                            <PriceCell
                                                editMode={tiersEditMode}
                                                value={row.price}
                                                editedValue={tiersEdited[row.id]}
                                                onChange={v => setTiersEdited(prev => ({ ...prev, [row.id]: v }))}
                                                currency={currency}
                                                saving={tiersSaving}
                                            />
                                        </td>
                                        <td>{val != null ? `${Number(val * 3).toFixed(2)} ${currency}` : '-'}</td>
                                        <td>{val != null ? `${Number(val * 6).toFixed(2)} ${currency}` : '-'}</td>
                                        <td>{val != null ? `${Number(val * 9).toFixed(2)} ${currency}` : '-'}</td>
                                        <td>{val != null ? `${Number(val * 12).toFixed(2)} ${currency}` : '-'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Section>

            {/* Section 2: Bundle / compulsory modules */}
            <Section
                title={trans('headings.compulsory_modules_bundle_price') || 'Compulsory Modules Bundle Price'}
                editMode={bundleEditMode}
                onEdit={handleBundleEdit}
                onSave={handleBundleSave}
                onCancel={handleBundleCancel}
                saving={bundleSaving}
                saveError={bundleSaveError}
                hasData={!bundleLoading && !bundleError}
            >
                {bundleLoading ? (
                    <p>{trans('labels.loading') || 'Loading...'}</p>
                ) : bundleError ? (
                    <p style={{ color: 'red' }}>{bundleError}</p>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>{trans('labels.price_per_month') || 'Price / Month'}</th>
                                <th>{trans('labels.price_per_3_months') || '3 Months'}</th>
                                <th>{trans('labels.price_per_6_months') || '6 Months'}</th>
                                <th>{trans('labels.price_per_9_months') || '9 Months'}</th>
                                <th>{trans('labels.price_per_12_months') || '12 Months'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <PriceCell
                                        editMode={bundleEditMode}
                                        value={bundlePrice}
                                        editedValue={bundlePriceEdited}
                                        onChange={v => setBundlePriceEdited(v)}
                                        currency={currency}
                                        saving={bundleSaving}
                                    />
                                </td>
                                {(() => {
                                    const val = bundleEditMode ? bundlePriceEdited : bundlePrice;
                                    return val != null && val !== '' ? (
                                        <>
                                            <td>{Number(val * 3).toFixed(2)} {currency}</td>
                                            <td>{Number(val * 6).toFixed(2)} {currency}</td>
                                            <td>{Number(val * 9).toFixed(2)} {currency}</td>
                                            <td>{Number(val * 12).toFixed(2)} {currency}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td>-</td><td>-</td><td>-</td><td>-</td>
                                        </>
                                    );
                                })()}
                            </tr>
                            {compulsoryModules.length > 0 && (
                                <tr>
                                    <td colSpan={5}>
                                        {trans('labels.compulsory_modules_are') || 'The compulsory modules are'}: {compulsoryModules.map(m => transModule(m.name)).join(', ')}.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </Section>

            {/* Section 3: Other modules */}
            <Section
                title={trans('headings.prices_per_optional_module') || 'Prices per Optional Module'}
                editMode={modulesEditMode}
                onEdit={handleModulesEdit}
                onSave={handleModulesSave}
                onCancel={handleModulesCancel}
                saving={modulesSaving}
                saveError={modulesSaveError}
                hasData={modules.length > 0}
            >
                {modulesLoading ? (
                    <p>{trans('labels.loading') || 'Loading...'}</p>
                ) : modulesError ? (
                    <p style={{ color: 'red' }}>{modulesError}</p>
                ) : modules.length === 0 ? (
                    <NoDataFound />
                ) : (
                    <table className="table">
                        <thead>{moduleTableHeaders}</thead>
                        <tbody>
                            {modules.map((row, idx) => renderModuleRow(
                                row, idx, modulesEdited,
                                (id, val) => setModulesEdited(prev => ({ ...prev, [id]: val })),
                                modulesEditMode, modulesSaving
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>
        </div>
    );
}

export default Configurator;
